import React, { useMemo } from 'react'
import { useNewTranslation } from 'hooks'
import { UploadType } from 'services/api/uploads'
import Field from 'components/Shared/Fields'
import { Section } from 'components/Campaign/Editor/TaskEditor/Shared'
import { RequirementBlockProps } from '.'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { TASK_TYPE_ENUM } from 'services/api/constants'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { FormikTextListGroup } from 'particles/TextInputs/TextList/Group'
import { usePermission } from 'hooks/permissions'
import { CampaignManagement } from 'components/Shared/Permission/enums'

const EXAMPLE_PHOTOS_LIMIT = 10

const TikTokPostAndInstagramReelAndXHSPost: React.FC<RequirementBlockProps> = ({
    type,
    stepType,
    requiredFieldNames,
    stepUuid,
    showBrief,
}) => {
    const [t] = useNewTranslation()
    const isEditableDefaultTag = usePermission(CampaignManagement.EditDefaultTags)
    const { videoRequirementText, requiredHashtagSubtitleText, exampleSubtitleText } = useMemo(() => {
        let videoRequirementKey = '',
            requiredHashtagSubtitleKey = '',
            exampleSubtitleKey = ''
        switch (type) {
            case TASK_TYPE_ENUM.IG_REEL:
                videoRequirementKey = 'campaignEditor.createTask.instagramReelRequirementHeader'
                requiredHashtagSubtitleKey = 'campaignEditor.createTask.addHashtagInstagramReelSubtext'
                exampleSubtitleKey = 'campaignEditor.createTask.uploadExamplePhotoInstagramReelSubtext'
                break
            case TASK_TYPE_ENUM.TIKTOK_POST:
                videoRequirementKey = 'campaignEditor.createTask.tikTokRequirementHeader'
                requiredHashtagSubtitleKey = 'campaignEditor.createTask.addHashtagTikTokSubtext'
                exampleSubtitleKey = 'campaignEditor.createTask.uploadExamplePhotoTikTokSubtext'
                break
            case TASK_TYPE_ENUM.XIAOHONGSHU_POST:
                videoRequirementKey = 'campaignEditor.createTask.XHSRequirementHeader'
                requiredHashtagSubtitleKey = 'appCampaign.campaignStep.XHSRequiredHashtagsSubtext'
                exampleSubtitleKey = 'campaignEditor.createTask.XHSAddPhotoExampleSubtext'
                break
        }

        return {
            videoRequirementText: t(videoRequirementKey),
            requiredHashtagSubtitleText: t(requiredHashtagSubtitleKey),
            exampleSubtitleText: t(exampleSubtitleKey),
        }
    }, [t, type])
    return (
        <>
            <Section title={videoRequirementText}>
                <CampaignEditorInputFieldContainer>
                    <FormikTextListGroup
                        title={t('campaignEditor.createTask.enterRequirementDoHeader')}
                        subtitle={t('campaignEditor.createTask.enterRequirementDoSubtext')}
                        placeholder={t('placeholderText.enterRequirementBulletPoint')}
                        isRequired={requiredFieldNames.includes('media_requirement_dos')}
                        name={`stepData.${stepType}.data.media_requirement_dos`}
                        isDefault
                        width="100%"
                        identifier={`${stepUuid}_${fieldIdentifiers.photoRequirementDos}`}
                        isReadOnly={showBrief}
                    />
                    <FormikTextListGroup
                        title={t('campaignEditor.createTask.enterRequirementDontHeader')}
                        subtitle={t('campaignEditor.createTask.addPhotoRequirementDontSubtext')}
                        placeholder={t('placeholderText.enterRequirementBulletPoint')}
                        buttonText={t('button.addDont')}
                        isRequired={requiredFieldNames.includes('media_requirement_donts')}
                        name={`stepData.${stepType}.data.media_requirement_donts`}
                        isDefault
                        width="100%"
                        identifier={`${stepUuid}_${fieldIdentifiers.photoRequirementDonts}`}
                        isReadOnly={showBrief}
                    />
                </CampaignEditorInputFieldContainer>
            </Section>
            <Section title={t('campaignEditor.createTask.addCaptionRequirementHeader')}>
                <CampaignEditorInputFieldContainer>
                    <FormikTextListGroup
                        title={t('campaignEditor.createTask.enterRequirementDoHeader')}
                        subtitle={t('campaignEditor.createTask.enterRequirementDoSubtext')}
                        placeholder={t('placeholderText.enterRequirementBulletPoint')}
                        isRequired={requiredFieldNames.includes('caption_requirement_dos')}
                        name={`stepData.${stepType}.data.caption_requirement_dos`}
                        isDefault
                        width="100%"
                        identifier={`${stepUuid}_${fieldIdentifiers.captionRequirementDos}`}
                        isReadOnly={showBrief}
                    />
                    <FormikTextListGroup
                        title={t('campaignEditor.createTask.enterRequirementDontHeader')}
                        subtitle={t('campaignEditor.createTask.addPhotoRequirementDontSubtext')}
                        placeholder={t('placeholderText.enterRequirementBulletPoint')}
                        buttonText={t('button.addDont')}
                        isRequired={requiredFieldNames.includes('caption_requirement_donts')}
                        name={`stepData.${stepType}.data.caption_requirement_donts`}
                        isDefault
                        width="100%"
                        identifier={`${stepUuid}_${fieldIdentifiers.captionRequirementDonts}`}
                        isReadOnly={showBrief}
                    />
                    <FormikTextListGroup
                        title={t('campaignEditor.createTask.addHashtagHeader')}
                        subtitle={requiredHashtagSubtitleText}
                        buttonText={t('button.addHashtag')}
                        name={`stepData.${stepType}.data.caption_requirement_hashtags`}
                        width="100%"
                        identifier={`${stepUuid}_${fieldIdentifiers.captionRequirementHashtags}`}
                        isReadOnly={showBrief}
                        inputIcon="hash"
                        isEditableDefaultTag={isEditableDefaultTag}
                    />
                </CampaignEditorInputFieldContainer>
            </Section>
            <Section title={t('campaignEditor.createTask.addPhotoExampleHeader')} subtitle={exampleSubtitleText}>
                <CampaignEditorInputFieldContainer>
                    <Field.ImageUploadField
                        title={t('campaignEditor.createTask.uploadFilesExampleHeader')}
                        subtitle={t('campaignEditor.createTask.uploadFilesExampleSubtext')}
                        presetType={UploadType.CAMPAIGN}
                        maxFiles={EXAMPLE_PHOTOS_LIMIT}
                        name={`stepData.${stepType}.data.example_photos`}
                        uploadContainerWidth="594px"
                        identifier={`${stepUuid}_${fieldIdentifiers.examplePhotos}`}
                        isReadOnly={showBrief}
                    />
                    <Field.TextAreaField
                        label={t('campaignEditor.createTask.enterDescriptionHeader')}
                        name={`stepData.${stepType}.data.example_photos_description`}
                        isRequired={requiredFieldNames.includes('example_photos_description')}
                        width="100%"
                        identifier={`${stepUuid}_${fieldIdentifiers.examplePhotosDescription}`}
                        isReadOnly={showBrief}
                    />
                </CampaignEditorInputFieldContainer>
            </Section>
        </>
    )
}

export default TikTokPostAndInstagramReelAndXHSPost
